import { useEffect, useState, useRef } from "react";

/**
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {function} props.setOpen
 * @param {React.RefObject} props.ref
 * @param {React.RefObject} props.buttonRef
 * @param {boolean | undefined} [disallow] - Optional boolean to disallow click away in certain circumstances
 * @param {React.RefObject[] | undefined} [ignoredRefs] - Optional refs outside dropdown that should not trigger click away
 * @returns {React.RefObject}
 */
export default function useClickAway(props) {
  const { isOpen, setOpen, ref, disallow, ignoredRefs } = props;

  const fallbackRef = useRef();
  const dropdownRef = ref || fallbackRef;
  const buttonRef = props.buttonRef || fallbackRef;

  const [moveFocus, setMoveFocus] = useState(true);

  useEffect(() => {
    if (disallow) {
      return;
    }

    if (isOpen) {
      const handleClickAway = (e) => {
        const clickedIgnoredRef =
          ignoredRefs &&
          ignoredRefs.some((ignoredRef) =>
            isOrContainsNode(ignoredRef?.current, e.target),
          );

        if (clickedIgnoredRef) {
          return;
        }

        if (!isOrContainsNode(dropdownRef.current, e.target)) {
          setOpen(false);
        }
      };

      const handleKeydown = (e) => {
        if (e.key === "Esc" || e.key === "Escape") {
          setOpen(false);

          if (moveFocus) {
            // requestAnimationFrame helps allow buttonRefs that are hidden when dropdown is open
            // to re-render before focus is set
            requestAnimationFrame(() => {
              buttonRef.current.focus();
            });
          }
        }
      };

      const handleFocusLoss = (e) => {
        const focusedIgnoredRef =
          ignoredRefs &&
          ignoredRefs.some((ignoredRef) =>
            isOrContainsNode(ignoredRef?.current, e.target),
          );

        setMoveFocus(
          isOrContainsNode(dropdownRef.current, e.target) || focusedIgnoredRef,
        );
      };

      document.addEventListener("click", handleClickAway, true);
      document.addEventListener("keydown", handleKeydown);
      document.addEventListener("focusin", handleFocusLoss);

      return () => {
        document.removeEventListener("click", handleClickAway, true);
        document.removeEventListener("keydown", handleKeydown);
        document.removeEventListener("focusin", handleFocusLoss);
      };
    }
  }, [
    dropdownRef,
    isOpen,
    setOpen,
    buttonRef,
    moveFocus,
    disallow,
    ignoredRefs,
  ]);

  return dropdownRef;
}

/* https://github.com/downshift-js/downshift/blob/046e4abf4468fead3bf3adadb1352279532345ed/src/utils.js#L41-L48*/
/**
 * @param {HTMLElement} parent the parent node
 * @param {HTMLElement} child the child node
 * @return {Boolean} whether the parent is the child or the child is in the parent
 */
function isOrContainsNode(parent, child) {
  return (
    !parent || parent === child || (parent.contains && parent.contains(child))
  );
}
